import { Component, forwardRef, inject, Injector, input, model, viewChild, OnInit, computed, effect, signal } from '@angular/core';
import { DxTextBoxComponent, DxTextBoxModule } from 'devextreme-angular';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl, ValidatorFn } from '@angular/forms';
import { ValueChangedEvent } from 'devextreme/ui/text_box';
import { DxTextBoxTypes } from 'devextreme-angular/ui/text-box';
import { TextEditorButton } from 'devextreme/common';
import { fromEvent, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SecretService } from 'src/app/shared/secret/services/secret.service';
import { DECORATOR_HOST, DecoratorHost } from 'src/app/controls/directives/decorator-host';

export enum PasswordMode {
  Hash = 'hash',
  Secret = 'secret',
}

function coercePasswordMode(value: PasswordMode | string | null | undefined): PasswordMode {
  let result = PasswordMode.Secret;
  const normalized = typeof value === 'string' ? value.toLowerCase() : '';
  if (normalized === 'hash') {
    result = PasswordMode.Hash;
  }
  return result;
}

export type PasswordComponentValue = {
  id: string | null;
  value: string | null;
};

function isPasswordComponentValue(obj: unknown): obj is PasswordComponentValue {
  if (obj === null || obj === undefined || typeof obj !== 'object') return false;
  const { id, value } = obj as PasswordComponentValue;
  return (id === null || typeof id === 'string') && (value === null || typeof value === 'string');
}

// Single reusable validator instance
const MIN_PASSWORD_LENGTH = 8;
const passwordMinLengthValidator: ValidatorFn = (control: FormControl) => {
  const pcv = control.value as PasswordComponentValue;
  const text = pcv?.value;
  return !text || text.length >= MIN_PASSWORD_LENGTH
    ? null // Skip when empty (let required handle emptiness)
    : { minlength: { requiredLength: MIN_PASSWORD_LENGTH, actualLength: text.length } };
};

@Component({
  selector: 'app-password',
  templateUrl: './password.component.html',
  styleUrl: './password.component.scss',
  imports: [DxTextBoxModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordComponent),
      multi: true
    },
    {
      provide: DECORATOR_HOST,
      useExisting: forwardRef(() => PasswordComponent)
    }
  ],
  host: {
    class: 'link-component'
  }
})
export class PasswordComponent implements OnInit, ControlValueAccessor, DecoratorHost {
  private dx = viewChild.required<DxTextBoxComponent>('dx');
  private secretService = inject(SecretService);
  private injector = inject(Injector);

  // Optional input signals
  allowShowPassword = input(false);
  mode = input(PasswordMode.Secret, { transform: coercePasswordMode });
  readonly = input(false);
  section = input<string>();
  validate = input(true); // Must explicitly disable validation
  value = model<PasswordComponentValue | null>(null);
  disabled = model(false);

  private readonly inputId = signal<string | null>(null);
  protected readonly inputAttr = computed<Record<string, string>>(() => this.inputId() ? { id: this.inputId(), autocomplete: this.autoComplete() } : { autocomplete: this.autoComplete() });

  // Public (non-input) signals
  control = computed(() => this.ngControl()?.control as FormControl);

  // Private signals
  private ngControl = signal<NgControl>(null);

  // Public computed signals
  autoComplete = computed(() => this.section() ? `section-${this.section()} new-password` : 'new-password');
  buttons = computed(() => [...(this.canRevealPassword() ? [this.revealButton()] : []), ...(this.canResetPassword() ? [this.resetButton()] : [])]);
  passwordMode = computed<DxTextBoxTypes.TextBoxType>(() => this.passwordRevealed() ? 'text' : 'password');
  placeholderText = computed(() => this.value()?.value ? null : this.value()?.id ? '••• A password is set •••' : null);
  valueText = computed(() => this.value()?.value ?? '');
  disabledOrReadonly = computed(() => this.disabled() || this.readonly());

  // Private computed signals
  private canResetPassword = computed(() => !this.disabledOrReadonly() && (!!this.value()?.value || !!this.value()?.id));
  private canRevealPassword = computed(() => !this.disabledOrReadonly() && (!!this.value()?.value || (!!this.value()?.id && this.mode() !== PasswordMode.Hash && this.allowShowPassword())));
  private passwordRevealed = signal(false);
  private lastFocusTarget = toSignal(fromEvent<FocusEvent>(document, 'focusin').pipe(map(ev => (ev.target instanceof HTMLElement ? ev.target : null))));
  private resetButton = computed(() => ({ name: 'reset', location: 'after', options: { icon: 'clear', stylingMode: 'text', type: 'normal', width: '9rem', text: 'Clear', onClick: () => this.clearPassword() } }) as TextEditorButton);
  private revealButton = computed(() => ({ name: 'reveal', location: 'after', options: { icon: this.passwordRevealed() ? 'eyeclose' : 'eyeopen', stylingMode: 'text', type: 'normal', width: '9rem', text: this.passwordRevealed() ? 'Hide' : 'Show', onClick: () => void this.togglePassword() } }) as TextEditorButton);
  private shouldPasswordBeRevealed = computed(() => {
    // Return true only when password reveal is requested _and_ focus is
    // inside the control; if focus goes outside the control this returns false
    let result = this.passwordRevealed();
    if (result) {
      const host = this.dx()?.instance?.element()?.get(0);
      const lastFocusTarget = this.lastFocusTarget();
      result = !host || !lastFocusTarget || (host === lastFocusTarget) || host.contains(lastFocusTarget);
    }
    return result;
  });

  constructor() {
    effect(() => {
      if (!this.shouldPasswordBeRevealed()) {
        this.passwordRevealed.set(false);
      }
    });

    effect(() => {
      const control = this.control();
      if (control) {
        if (this.validate()) {
          this.applyPasswordMinLength(control);
        }
        else {
          this.removePasswordMinLength(control);
        }
      }
    });
  }

  ngOnInit(): void {
    try {
      this.ngControl.set(this.injector.get(NgControl));
    }
    catch {
      // No NgControl
    }
  }

  get isValid(): boolean {
    return !this.control() || (this.control().invalid !== true || (this.control().pristine && this.control().untouched));
  }

  onValueChanged(e: ValueChangedEvent): void {
    const newText: string = typeof e.value === 'string' ? e.value : null;
    const current = this.value();
    if (!current || current.value !== newText) {
      let nextValue: PasswordComponentValue = { id: current?.id ?? null, value: newText };
      if (!nextValue.id && !nextValue.value) {
        nextValue = null;
      }
      this.onChange(nextValue);
      this.value.set(nextValue);
    }
  };

  private clearPassword() {
    this.onChange(null);
    this.value.set(null);
    this.passwordRevealed.set(false);
  }

  private async togglePassword(): Promise<void> {
    this.passwordRevealed.update(e => !e);

    if (this.passwordRevealed()) {
      const current = this.value();
      if (current && !!current.id && !current.value) {
        try {
          const secretText = await this.secretService.getSecret(current.id);
          this.value.set({ id: current.id, value: secretText });
        }
        catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (error?.status === 404) {
            this.value.set({ id: null, value: current.id });
          }
        }
      }
    }
  }

  private applyPasswordMinLength(control: FormControl): void {
    if (!control.hasValidator(passwordMinLengthValidator)) {
      control.addValidators(passwordMinLengthValidator);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private removePasswordMinLength(control: FormControl): void {
    if (control.hasValidator(passwordMinLengthValidator)) {
      control.removeValidators(passwordMinLengthValidator);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }


  /*
   *  ControlValueAccessor interface methods
   */

  writeValue(obj: unknown): void {
    if (obj !== null && !isPasswordComponentValue(obj)) {
      console.warn('[PasswordComponent] writeValue received invalid value:', { obj, control: this.ngControl() });
      obj = null;
    }
    this.value.set(obj as PasswordComponentValue ?? null);
  }

  private onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  };

  private onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  };

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }


  /*
   * DecoratorHost interface methods
   */

  setInputId(inputId: string | null): void {
    this.inputId.set(inputId);
  }

  getInputId(): string | null {
    return this.inputId();
  }
}
